import Breadcrumb from "components/Common/Breadcrumb"
import dayjs from "dayjs"
import {
  cancelManagedAddon,
  getManagedAddons,
} from "helpers/backendHelpers/manageSubscription"
import React, { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  UncontrolledTooltip,
} from "reactstrap"
import DynamicTable from "components/Common/DynamicTable"
import { DateRangePicker } from "rsuite"
import { getPredefinedDateRanges } from "helpers/dateRanges"

const FILTERS = [
  { label: "All", value: "all" },
  { label: "Expired", value: "expired" },
  { label: "Cancelled", value: "cancelled" },
]

const roundCurrency = value => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100

const getAddonItems = row => {
  if (!row) return []
  if (Array.isArray(row.addonItems) && row.addonItems.length) return row.addonItems
  return [row]
}

const isDurationItem = item => item && item.recordType === "duration"

const isCreditItem = item => item && item.recordType === "credit"

const getTaxRate = row => {
  const taxableAmount = Number((row && row.taxableAmount) || 0)
  const taxAmount = Number((row && row.taxAmount) || 0)
  if (taxableAmount > 0) return taxAmount / taxableAmount
  return 0.18
}

const ManageAddon = () => {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [search, setSearch] = useState("")
  const [appliedSearch, setAppliedSearch] = useState("")
  const [selectedFilter, setSelectedFilter] = useState("all")
  const [date, setDate] = useState({
    startDate: null,
    endDate: null,
  })
  const [viewRow, setViewRow] = useState(null)
  const [cancelRow, setCancelRow] = useState(null)
  const [cancelReason, setCancelReason] = useState("Add-on cancellation refund")
  const [deductUsedDays, setDeductUsedDays] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)

  const pageCount = Math.max(1, Math.ceil(totalRecords / pageSize))

  const fetchRows = async () => {
    try {
      setLoading(true)
      const response = await getManagedAddons({
        page: pageIndex + 1,
        limit: pageSize,
        search: appliedSearch,
        filterValue: selectedFilter,
        startDate: date.startDate,
        endDate: date.endDate,
      })
      const payload = (response && response.payload) || {}
      setRows(payload.records || [])
      setTotalRecords(payload.totalRecords || 0)
    } catch (error) {
      setRows([])
      setTotalRecords(0)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRows()
  }, [pageIndex, pageSize, appliedSearch, selectedFilter, date])

  const openCancelModal = row => {
    setCancelRow(row)
    setCancelReason("Add-on cancellation refund")
    setDeductUsedDays(false)
  }

  const closeCancelModal = () => {
    setCancelRow(null)
    setCancelReason("Add-on cancellation refund")
    setDeductUsedDays(false)
    setCancelLoading(false)
  }

  const getCancelPreview = useMemo(() => {
    if (!cancelRow) return null
    const invoiceDate = cancelRow.invoiceDate || cancelRow.purchaseDate
    const today = dayjs()
    const invoiceDay = dayjs(invoiceDate)
    const refundEndDate = invoiceDay.add(29, "day")
    const withinWindow = today.isBefore(refundEndDate.endOf("day")) || today.isSame(refundEndDate, "day")
    const sameGstMonth = invoiceDay.isSame(today, "month") && invoiceDay.isSame(today, "year")
    const taxRate = getTaxRate(cancelRow)
    const usedDaysFromInvoice = Math.max(0, today.startOf("day").diff(invoiceDay.startOf("day"), "day"))

    const itemPreviews = getAddonItems(cancelRow).map(item => {
      const taxableAmount = Number(
        item.taxableAmount != null
          ? item.taxableAmount
          : item.price != null
            ? item.price
            : (cancelRow.taxableAmount != null ? cancelRow.taxableAmount : 0)
      )
      const itemTaxAmount = Number(item.taxAmount != null ? item.taxAmount : roundCurrency(taxableAmount * taxRate))
      const totalAmount = Number(item.totalAmount != null ? item.totalAmount : roundCurrency(taxableAmount + itemTaxAmount))

      if (isCreditItem(item)) {
        const creditsPurchased = Number(item.creditsPurchased || 0)
        const refundCredits = Number(item.refundCredits || item.creditsAvailable || 0)
        const usedCredits = Number(item.creditsUsed || Math.max(0, creditsPurchased - refundCredits))
        const pricePerCredit = creditsPurchased > 0 ? taxableAmount / creditsPurchased : 0
        const refundTaxableAmount = roundCurrency(refundCredits * pricePerCredit)
        const refundTaxAmount = roundCurrency(refundTaxableAmount * taxRate)

        return {
          ...item,
          basis: "credit",
          taxableAmount,
          taxAmount: itemTaxAmount,
          totalAmount,
          // Credit-based add-ons always require a credit note on cancellation.
          requiresCreditNote: true,
          refundTaxableAmount,
          refundTaxAmount,
          refundAmount: roundCurrency(refundTaxableAmount + refundTaxAmount),
          usedCredits,
          refundCredits,
          creditsPurchased,
          usedDays: 0,
          usedAmount: 0,
        }
      }

      const durationDays = Number(item.durationDays || cancelRow.durationDays || 365)
      const shouldDeduct = !withinWindow || deductUsedDays
      const usedDays = shouldDeduct ? Math.min(usedDaysFromInvoice, durationDays) : 0
      const dailyRate = shouldDeduct && durationDays > 0 ? taxableAmount / durationDays : 0
      const usedAmount = roundCurrency(dailyRate * usedDays)
      const refundTaxableAmount = shouldDeduct ? roundCurrency(Math.max(0, taxableAmount - usedAmount)) : taxableAmount
      const refundTaxAmount = shouldDeduct ? roundCurrency(refundTaxableAmount * taxRate) : roundCurrency(totalAmount - taxableAmount)

      return {
        ...item,
        basis: "duration",
        taxableAmount,
        taxAmount: itemTaxAmount,
        totalAmount,
        requiresCreditNote: shouldDeduct || !sameGstMonth,
        refundTaxableAmount,
        refundTaxAmount,
        refundAmount: roundCurrency(refundTaxableAmount + refundTaxAmount),
        usedDays,
        durationDays,
        usedAmount,
        usedCredits: 0,
        refundCredits: 0,
        creditsPurchased: 0,
      }
    })

    const hasDuration = itemPreviews.some(item => item.basis === "duration")
    const hasCredit = itemPreviews.some(item => item.basis === "credit")
    const originalTaxableAmount = roundCurrency(Number(cancelRow.taxableAmount || 0))
    const originalTaxAmount = roundCurrency(Number(cancelRow.taxAmount || 0))
    const originalTotalAmount = roundCurrency(
      Number(cancelRow.totalAmount || originalTaxableAmount + originalTaxAmount)
    )

    return {
      invoiceDate,
      refundEndDate,
      withinWindow,
      sameGstMonth,
      requiresCreditNote: itemPreviews.some(item => item.requiresCreditNote),
      originalTaxableAmount,
      originalTaxAmount,
      originalTotalAmount,
      refundTaxableAmount: roundCurrency(itemPreviews.reduce((sum, item) => sum + item.refundTaxableAmount, 0)),
      refundTaxAmount: roundCurrency(itemPreviews.reduce((sum, item) => sum + item.refundTaxAmount, 0)),
      refundAmount: roundCurrency(itemPreviews.reduce((sum, item) => sum + item.refundAmount, 0)),
      usedDays: Math.max(0, ...itemPreviews.map(item => item.usedDays || 0)),
      durationDays: Math.max(0, ...itemPreviews.map(item => item.durationDays || 0)),
      usedAmount: roundCurrency(itemPreviews.reduce((sum, item) => sum + item.usedAmount, 0)),
      usedCredits: itemPreviews.reduce((sum, item) => sum + Number(item.usedCredits || 0), 0),
      refundCredits: itemPreviews.reduce((sum, item) => sum + Number(item.refundCredits || 0), 0),
      creditsPurchased: itemPreviews.reduce((sum, item) => sum + Number(item.creditsPurchased || 0), 0),
      proratedRefund: itemPreviews.some(item => item.basis === "duration" && Number(item.usedDays || 0) > 0),
      creditRefund: itemPreviews.some(item => item.basis === "credit"),
      hasDuration,
      hasCredit,
      basis: hasDuration && hasCredit ? "mixed" : hasCredit ? "credit" : "duration",
      itemPreviews,
    }
  }, [cancelRow, deductUsedDays])

  const handleCancelAddon = async () => {
    if (!cancelRow || cancelLoading) return
    try {
      setCancelLoading(true)
      await cancelManagedAddon({
        recordType: cancelRow.recordType,
        addonSubscriptionId: cancelRow.addonSubscriptionId,
        paymentId: cancelRow.paymentId,
        paymentItemId: cancelRow.paymentItemId,
        paymentItemSource: cancelRow.paymentItemSource,
        addonId: cancelRow.addonId,
        reason: cancelReason,
        deductUsedDays,
      })
      closeCancelModal()
      fetchRows()
    } catch (error) {
      window.alert((error && error.response && error.response.data && error.response.data.message) || (error && error.message) || "Unable to cancel add-on")
      setCancelLoading(false)
    }
  }

  const openUrl = url => {
    if (url) window.open(url, "_blank")
  }

  const applySearch = () => {
    setPageIndex(0)
    setAppliedSearch(search.trim())
  }

  const columns = useMemo(
    () => [
      {
        Header: "Username",
        accessor: "userName",
        filterable: true,
        Cell: cellProps => {
          const row = cellProps.row.original
          return (
            <>
              <div className="fw-semibold">{row.userName || "-"}</div>
              {row.mobileNumber ? <small className="text-muted">{row.mobileNumber}</small> : null}
            </>
          )
        },
      },
      {
        Header: "Invoice No.",
        accessor: "invoiceNo",
        filterable: true,
        Cell: cellProps => cellProps.row.original.invoiceNo || "-",
      },
      {
        Header: "Invoice Date",
        accessor: "invoiceDate",
        filterable: true,
        Cell: cellProps => {
          const row = cellProps.row.original
          return row.invoiceDate ? dayjs(row.invoiceDate).format("DD/MM/YYYY") : "-"
        },
      },
      {
        Header: "Add-on",
        accessor: "addonName",
        filterable: true,
        Cell: cellProps => {
          const row = cellProps.row.original
          return (
            <>
              <div>{row.addonName || "-"}</div>
              {row.isEwayCreditBased && (
                <small className="text-muted">
                  Used Credits: {Number(row.creditsUsed || 0)} | Remaining Credits: {Number(row.creditsAvailable || 0)}
                </small>
              )}
            </>
          )
        },
      },
      {
        Header: "Basis",
        accessor: "basis",
        filterable: true,
        Cell: cellProps => cellProps.row.original.basis || "-",
      },
      {
        Header: "Amount",
        accessor: "totalAmount",
        filterable: true,
        Cell: cellProps => `Rs. ${roundCurrency(cellProps.row.original.totalAmount).toFixed(2)}`,
      },
      {
        Header: "Expiration Date",
        accessor: "expiryDate",
        filterable: true,
        Cell: cellProps => {
          const expiryDate = cellProps.row.original.expiryDate
          return expiryDate ? dayjs(expiryDate).format("DD/MM/YYYY") : "-"
        },
      },
      {
        Header: "Status",
        accessor: "status",
        disableFilters: true,
        Cell: cellProps => {
          const status = cellProps.row.original.status
          if (status === "cancelled") return <Badge color="danger">Cancelled</Badge>
          if (status === "paid") return <Badge color="success">Active</Badge>
          if (status === "expired") return <Badge color="secondary">Expired</Badge>
          return <Badge color="warning">Inactive</Badge>
        },
      },
      {
        Header: "Expired",
        accessor: "isExpired",
        disableFilters: true,
        Cell: cellProps => {
          const row = cellProps.row.original
          const isExpired = row.status === "cancelled" || row.status === "expired" || Boolean(
            row.expiryDate && dayjs(row.expiryDate).isBefore(dayjs(), "day")
          )
          return isExpired ? <Badge color="secondary">Yes</Badge> : <Badge color="success">No</Badge>
        },
      },
      {
        Header: "Action",
        accessor: "action",
        disableFilters: true,
        Cell: cellProps => {
          const row = cellProps.row.original
          const tooltipId = String(row._id || row.paymentItemId || row.invoiceNo || "").replace(/[^a-zA-Z0-9_-]/g, "")
          return (
            <div className="d-flex gap-3 align-items-center">
              <i
                className={`bx bxs-file-pdf font-size-24 ${row.invoiceUrl ? "cursor-pointer text-danger" : "text-muted"}`}
                id={`addonInvoiceTooltip-${tooltipId}`}
                onClick={() => openUrl(row.invoiceUrl)}
              />
              <UncontrolledTooltip placement="top" target={`addonInvoiceTooltip-${tooltipId}`}>
                Invoice
              </UncontrolledTooltip>

              <Link
                to="#"
                className="text-primary"
                onClick={() => setViewRow(row)}
              >
                <i className="mdi mdi-eye font-size-18" id={`addonViewTooltip-${tooltipId}`} />
                <UncontrolledTooltip placement="top" target={`addonViewTooltip-${tooltipId}`}>
                  View
                </UncontrolledTooltip>
              </Link>

              {row.status === "paid" && !row.hasCreditRecharge && (
                <>
                  <Link
                    to="#"
                    className="text-danger"
                    onClick={() => openCancelModal(row)}
                  >
                    <i className="mdi mdi-cancel font-size-18" id={`addonCancelTooltip-${tooltipId}`} />
                  </Link>
                  <UncontrolledTooltip placement="top" target={`addonCancelTooltip-${tooltipId}`}>
                    Cancel
                  </UncontrolledTooltip>
                </>
              )}

                {row.creditNoteUrl && (
                  <>
                    <a
                      href={row.creditNoteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-danger"
                      id={`addonCreditNoteTooltip-${tooltipId}`}
                    >
                      <i className="mdi mdi-file-document font-size-18" />
                    </a>
                    <UncontrolledTooltip placement="top" target={`addonCreditNoteTooltip-${tooltipId}`}>
                      Credit Note
                    </UncontrolledTooltip>
                  </>
                )}
              </div>
            )
          },
      },
    ],
    []
  )

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Manage Add-on" breadcrumbItem="Manage Add-on" />
          <Row className="subscription-page">
            <Col xs="12">
              <Card>
                <CardBody>
                  <div
                    className="btn-group btn-group-example mb-3"
                    role="group"
                    style={{ position: "absolute", left: "35%", zIndex: "1" }}
                  >
                    {FILTERS.map(filter => (
                      <button
                        key={filter.value}
                        onClick={() => {
                          setPageIndex(0)
                          setSelectedFilter(filter.value)
                        }}
                        type="button"
                        className={`btn btn-outline-primary w-sm ${
                          selectedFilter === filter.value
                            ? "active-subscription-status"
                            : ""
                        }`}
                      >
                        {filter.label}
                      </button>
                    ))}
                  </div>

                  <div style={{ position: "absolute", right: 0, zIndex: "1" }}>
                    <DateRangePicker
                      ranges={getPredefinedDateRanges()}
                      placement="bottomEnd"
                      onClean={() => {
                        setPageIndex(0)
                        setDate({ startDate: null, endDate: null })
                      }}
                      value={
                        date.startDate && date.endDate
                          ? [new Date(date.startDate), new Date(date.endDate)]
                          : null
                      }
                      placeholder="Expiry Date"
                      className="range-picker mb-2 date-picker-div"
                      onChange={value => {
                        if (value !== null) {
                          const [startDateValue, endDateValue] = value
                          const startDate =
                            dayjs(startDateValue)
                              .format("YYYY-MM-DD")
                              .split("T")[0] + "T00:00:00.000Z"
                          const endDate =
                            dayjs(endDateValue)
                              .format("YYYY-MM-DD")
                              .split("T")[0] + "T00:00:00.000Z"
                          setPageIndex(0)
                          setDate({ startDate, endDate })
                        }
                      }}
                      showOneCalendar
                      format="dd/MM/yyyy"
                    />
                  </div>

                  <DynamicTable
                    columns={columns}
                    data={rows}
                    isLoading={loading}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    pageCount={pageCount}
                    canPreviousPage={pageIndex > 0}
                    canNextPage={pageIndex < pageCount - 1}
                    gotoPage={setPageIndex}
                    previousPage={() => setPageIndex(pageIndex - 1)}
                    nextPage={() => setPageIndex(pageIndex + 1)}
                    setPageSize={size => {
                      setPageIndex(0)
                      setPageSize(size)
                    }}
                    state={{ pageIndex, pageSize }}
                    search={search}
                    handleSearchChange={event => {
                      const value = event.target.value
                      setSearch(value)
                      if (value.trim() === "") {
                        setPageIndex(0)
                        setAppliedSearch("")
                      }
                    }}
                    handleSearchClick={applySearch}
                    totalRecords={totalRecords}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <Modal isOpen={!!viewRow} toggle={() => setViewRow(null)} centered>
        <ModalHeader toggle={() => setViewRow(null)}>Add-on Details</ModalHeader>
        <ModalBody>
          {viewRow && (
            <div className="d-grid gap-2">
              <div><strong>Customer:</strong> {viewRow.userName || "-"}</div>
              <div><strong>Firm:</strong> {viewRow.firmName || "-"}</div>
              <div><strong>Add-on:</strong> {viewRow.addonName || "-"}</div>
              <div><strong>Basis:</strong> {viewRow.basis || "-"}</div>
              <div><strong>Invoice:</strong> {viewRow.invoiceNo || "-"}</div>
              <div><strong>Invoice Date:</strong> {viewRow.invoiceDate ? dayjs(viewRow.invoiceDate).format("DD/MM/YYYY") : "-"}</div>
              <div><strong>Expiry:</strong> {viewRow.expiryDate ? dayjs(viewRow.expiryDate).format("DD/MM/YYYY") : "-"}</div>
              {getAddonItems(viewRow).length > 1 && (
                <div className="border rounded p-2">
                  <div className="fw-semibold mb-2">Invoice Items</div>
                  {getAddonItems(viewRow).map(item => (
                    <div key={`${item.recordType}-${item.paymentItemId || item.addonId}`} className="d-flex justify-content-between border-bottom py-1">
                      <span>{item.planName || item.addonName || "-"}</span>
                      <span>{item.basis || item.recordType}</span>
                    </div>
                  ))}
                </div>
              )}
              {getAddonItems(viewRow).some(isCreditItem) && (
                <div className="border rounded p-2">
                  <div className="fw-semibold mb-2">Credit Usage</div>
                  {getAddonItems(viewRow).filter(isCreditItem).map(item => (
                    <div key={item.paymentItemId || item.addonId} className="small mb-2">
                      <div><strong>{item.planName || item.addonName || "Credit Add-on"}</strong></div>
                      <div>Purchased: {item.creditsPurchased || 0}</div>
                      <div>Used: {item.creditsUsed || 0}</div>
                      <div>Available: {item.creditsAvailable || 0}</div>
                    </div>
                  ))}
                </div>
              )}
              <div><strong>Amount:</strong> Rs. {roundCurrency(viewRow.totalAmount).toFixed(2)}</div>
              <div><strong>Status:</strong> {String(viewRow.status || "-").toUpperCase()}</div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setViewRow(null)}>Close</Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!cancelRow} toggle={closeCancelModal}>
        <ModalHeader toggle={closeCancelModal}>Cancel Add-on</ModalHeader>
        <ModalBody>
          {cancelRow && getCancelPreview && (
            <>
              <p className="mb-2"><strong>User:</strong> {cancelRow.userName || "-"}</p>
              <p className="mb-2"><strong>Add-on:</strong> {cancelRow.addonName || "-"}</p>
              <p className="mb-2"><strong>Invoice No:</strong> {cancelRow.invoiceNo || "-"}</p>
              <p className="mb-2">
                <strong>Invoice Date:</strong>{" "}
                {getCancelPreview.invoiceDate ? dayjs(getCancelPreview.invoiceDate).format("DD/MM/YYYY") : "-"}
              </p>
              <p className="mb-2">
                <strong>Plan Base Amount:</strong> Rs. {getCancelPreview.originalTaxableAmount.toFixed(2)}
              </p>
              <p className="mb-2"><strong>GST:</strong> Rs. {getCancelPreview.originalTaxAmount.toFixed(2)}</p>
              <p className="mb-2"><strong>Total Paid:</strong> Rs. {getCancelPreview.originalTotalAmount.toFixed(2)}</p>

              {getCancelPreview.hasDuration && getCancelPreview.withinWindow && (
                <div className="mb-3">
                  <Label>Used Days Deduction</Label>
                  <Input
                    type="select"
                    value={deductUsedDays ? "deduct" : "full"}
                    onChange={event => setDeductUsedDays(event.target.value === "deduct")}
                  >
                    <option value="full">No deduction - full refund</option>
                    <option value="deduct">Deduct used days</option>
                  </Input>
                </div>
              )}

              {getCancelPreview.hasDuration && !getCancelPreview.withinWindow && (
                <div className="alert alert-warning py-2">
                  Used days deduction is mandatory because cancellation is outside the 30-day refund window.
                </div>
              )}

              {getCancelPreview.itemPreviews.map((item, index) => (
                <div className="alert alert-info py-2" key={`${item.paymentItemId || item.addonId || index}-refund`}>
                  <div className="fw-semibold mb-1">{item.planName || item.addonName || "Add-on"}</div>
                  {item.basis === "duration" ? (
                    <>
                      <div><strong>Unused Days Refund:</strong> {item.usedDays} of {item.durationDays || "-"} days used</div>
                      <div>Used Base Amount: Rs. {item.usedAmount.toFixed(2)}</div>
                    </>
                  ) : (
                    <>
                      <div><strong>Unused Credits Refund:</strong> {item.usedCredits} of {item.creditsPurchased || "-"} credits used</div>
                      <div>Refund Credits: {item.refundCredits}</div>
                    </>
                  )}
                  <div>Refund Base Amount: Rs. {item.refundTaxableAmount.toFixed(2)}</div>
                  <div>Refund GST: Rs. {item.refundTaxAmount.toFixed(2)}</div>
                  <div><strong>Refund Amount: Rs. {item.refundAmount.toFixed(2)}</strong></div>
                </div>
              ))}

              <p className="mb-2"><strong>Refund Amount:</strong> Rs. {getCancelPreview.refundAmount.toFixed(2)}</p>
              <p className="mb-2">
                <strong>Refund Window:</strong>{" "}
                {getCancelPreview.invoiceDate ? dayjs(getCancelPreview.invoiceDate).format("DD/MM/YYYY") : "-"} to{" "}
                {getCancelPreview.refundEndDate ? dayjs(getCancelPreview.refundEndDate).format("DD/MM/YYYY") : "-"}
              </p>
              <p className="mb-2">
                <strong>Action:</strong>{" "}
                {getCancelPreview.requiresCreditNote
                  ? "Generate credit note, cancel invoice and deactivate add-on"
                  : "Cancel invoice and deactivate add-on"}
              </p>
              <div className="alert alert-warning">
                Refund money is record-only here. Process actual refund manually.
              </div>

              <Label>Reason</Label>
              <Input
                type="textarea"
                rows={3}
                value={cancelReason}
                onChange={event => setCancelReason(event.target.value)}
              />
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeCancelModal} disabled={cancelLoading}>Close</Button>
          <Button color="danger" onClick={handleCancelAddon} disabled={cancelLoading}>
            {cancelLoading ? "Cancelling..." : "Confirm Cancel"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  )
}

export default ManageAddon
